<template>
    <client-only>
        <v-page-product-availability-button v-if="deliveryEstimate"
            icon="box"
            actionless>
            {{ deliveryEstimate }}
        </v-page-product-availability-button>

        <v-page-product-availability-button v-if="cheapestDeliveryPrice !== undefined"
            icon="shipping"
            @click="isDeliveryOpen = true">
            <template v-if="promotionInfo?.deliveryPricingInfoOverride">
                {{ promotionInfo.deliveryPricingInfoOverride }}
            </template>

            <template v-else>
                {{ t('product-delivery', { amount: formatCurrency(cheapestDeliveryPrice) }) }} - {{ t('free-pickup') }}
            </template>
        </v-page-product-availability-button>

        <v-page-product-availability-button v-else
            icon="shipping"
            @click="isDeliveryOpen = true">
            {{ t('free-pickup') }}
        </v-page-product-availability-button>

        <v-page-product-availability-button v-if="outletGroupingAttributeId"
            icon="shop"
            @click="openAvailabilityModal">
            {{ modelOutletsCount >= 6
                ? t('product-availability', { amount: modelOutletsCount })
                : t('no-availability')
            }}
        </v-page-product-availability-button>
    </client-only>

    <!-- <v-page-product-availability-button icon="location"
        disabled
        @click="isPostcodeOpen = true">
        Alueesi saatavuus- ja toimitustiedot
    </v-page-product-availability-button> -->

    <v-page-product-delivery-modal v-model="postalCode"
        :is-open="isDeliveryOpen"
        :groups="shippingMethodGroups"
        :is-free-shipping="isEligibleForFreeShipping"
        @close="isDeliveryOpen = false" />

    <v-page-product-availability-modal v-if="outletGroupingAttributeId"
        :is-buyable="properties.product.inventory.isAvailableForPurchase"
        :is-open="isAvailabilityOpen"
        :outlets="outlets"
        @close="isAvailabilityOpen = false" />

    <!-- <v-page-product-availability-postcode :is-open="isPostcodeOpen"
        @close="isPostcodeOpen = false"></v-page-product-availability-postcode> -->
</template>

<script lang="ts" setup>
import { useQuery } from '@urql/vue';
import { uniqBy } from 'es-toolkit';
import { useGtm } from '@gtm-support/vue-gtm';
import {
    ShippingMethodType,
    type SimpleProductDataFragment,
    type ViewProductDetailsQuery,
    ViewProductShippingMethodsDocument,
    type ViewProductShippingMethodsQuery,
    type ViewProductShippingMethodsQueryVariables,
} from '~~/graphql';

type Product = Extract<ViewProductShippingMethodsQuery['product'], { __typename: 'SimpleProduct' }>;
type ProductDetails = Extract<ViewProductDetailsQuery['product'], { __typename: 'SimpleProduct' }>;

type ShippingMethod = Product['availableShippingMethods'][0];
type ShippingMethodGroup = NonNullable<ShippingMethod['group']>;
type ShippingMethodGroupCombined = ShippingMethodGroup & {
    children: ShippingMethod[]
};

interface PromotionInfo {
    deliveryPricingInfoOverride: string
}

const properties = defineProps<{
    product: SimpleProductDataFragment
    productDetails?: ProductDetails
    promotionInfo?: PromotionInfo
}>();

const { t } = useI18n();
const { format: formatCurrency } = useCurrency();
const { format: formatDeliveryEstimateText } = useDeliveryEstimateText();

const deliveryEstimate = computed(() => formatDeliveryEstimateText(properties.product.inventory, 'product'));

const isDeliveryOpen = ref(false);
const isAvailabilityOpen = ref(false);
const postalCode = ref('');

const details = computed(() => properties.productDetails);

function postalCodeIsValid(value: string) {
    return /^\d{5}$/.test(value);
}

const { data: shippingData } = useQuery<ViewProductShippingMethodsQuery, ViewProductShippingMethodsQueryVariables>({
    query: ViewProductShippingMethodsDocument,
    variables: computed(() => ({
        id: properties.product.id,
        postalCode: postalCodeIsValid(postalCode.value) ? postalCode.value : t('postal-code-to-query'),
    })),
});

const isEligibleForFreeShipping = computed(() => (
    shippingData.value?.product.__typename === 'SimpleProduct'
        ? shippingData.value.product.isEligibleForFreeShipping
        : false
));

const shippingMethods = computed(() => (
    shippingData.value?.product.__typename === 'SimpleProduct'
        ? shippingData.value.product.availableShippingMethods
        : []
));

const shippingMethodGroups = computed<ShippingMethodGroupCombined[]>(() => (
    uniqBy(
        shippingMethods.value
            .map((method) => method.group)
            .filter((group): group is ShippingMethodGroup => !!group),
        (group) => group.id,
    ).map((group: ShippingMethodGroup) => ({
        ...group,
        children: shippingMethods.value.filter((method) => method.group?.id === group.id),
    }))
));

// Get the cheapest delivery price from the shipping methods.
// See '[product].vue'
const cheapestDeliveryPrice = computed<number | undefined>(() => (
    (shippingMethods.value
        .filter((method) => ![ShippingMethodType.PickUpFromStore].includes(method.type))
        .map((method) => {
            if (isEligibleForFreeShipping.value && method.freeShippingPriceWithServices) {
                return Number(method.freeShippingPriceWithServices.amount);
            }

            return Number(method.originalPriceWithServices.amount);
        })
        .sort((a, b) => a - b)
        .at(0)
    )
));

const {
    public: {
        outletGroupingAttributeId,
    },
} = useRuntimeConfig();

const { outlets } = useOutletStocks(details);

const modelOutletsCount = computed(() => outlets.value.filter((outlet) => outlet.modelQuantity > 0).length);

const gtm = useGtm();

function openAvailabilityModal() {
    // Show availability modal and track event.
    isAvailabilityOpen.value = !isAvailabilityOpen.value;
    gtm?.trackEvent({
        /* eslint-disable @typescript-eslint/naming-convention */
        event: 'check_availability',
        _clear: true,
        /* eslint-enable @typescript-eslint/naming-convention */
    });
}
</script>

<i18n lang="json">
{
    "et": {
        "product-delivery": "Kohaletoimetamine alates {amount}",
        "product-availability": "Toode kohapeal {amount}/5 kaupluses",
        "no-availability": "Kontrolli kaupluste saadavust",
        "free-pickup": "Järeltulemine on alati tasuta!",
        "postal-code-to-query": "10001"
    },
    "fi": {
        "product-delivery": "Toimitus alkaen {amount}",
        "product-availability": "Esillä {amount}/46 myymälässä",
        "no-availability": "Tarkista myymäläsaatavuus",
        "free-pickup": "Nouto on aina ilmainen!",
        "postal-code-to-query": "00100"
    }
}
</i18n>
